import { Injectable, OnDestroy } from '@angular/core';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { EventDetailsConfig, SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { getAnimationIconForSport } from '@frontend/sports/common/core/feature/icon';
import { WindowEventService } from '@frontend/sports/common/core/utils/window-event';
import { filterSportsEmitLast } from '@frontend/sports/host-app/sports-product/feature/utils';
import { UrlService } from '@frontend/vanilla/core';
import { findIndex } from 'lodash-es';
import { BehaviorSubject, Observable, Subscription, firstValueFrom } from 'rxjs';

import { ColumnLayoutService } from '../../layout/column-layout.service';
import { TabBarItem } from '../../tab-bar/tab-bar.models';
import { FeatureTab, FeatureUrl, MediaTab } from '../event-details-common.models';
import { EventDetailsHelperService } from '../event-details-helper.service';

@Injectable({ providedIn: 'root' })
export class HeaderNavigationService implements OnDestroy {
    private _selectedTab: BehaviorSubject<TabBarItem | undefined>;
    private _tabs: TabBarItem[];
    private resizeSubscription: Subscription;
    private mediaTabsFallback: string[] = [MediaTab.Scoreboard, MediaTab.Video, MediaTab.Simulation, MediaTab.Statistics];

    constructor(
        private urlService: UrlService,
        private windowEventService: WindowEventService,
        private columnLayoutService: ColumnLayoutService,
        private eventDetailsHelperService: EventDetailsHelperService,
        private sitecoreEventDetails: SitecoreEventDetailsConfig,
        private eventDetailsConfig: EventDetailsConfig,
    ) {
        this._selectedTab = new BehaviorSubject<TabBarItem | undefined>(undefined);
    }

    async init(event: EventModel, isScoreDefault: boolean = false): Promise<TabBarItem[]> {
        await firstValueFrom(this.sitecoreEventDetails.whenReady);
        this._selectedTab.next(undefined);
        this._tabs = this.getFeatureTabs(event, isScoreDefault);
        const activeIndex = Math.max(this.getActiveIndex(), 0);
        this.setActiveIndex(activeIndex);
        this.resizeSubscription = this.windowEventService.resize
            .asObservable()
            .pipe(filterSportsEmitLast())
            .subscribe(() => this.setMiddleColumnTab());
        this.setMiddleColumnTab();

        return [...this._tabs];
    }

    ngOnDestroy(): void {
        this.resizeSubscription?.unsubscribe();
    }

    get selectedTab(): Observable<TabBarItem | undefined> {
        return this._selectedTab.asObservable();
    }

    updateSelected(item?: TabBarItem): void {
        const itemIndex = findIndex(this._tabs, (tab) => tab.id === item?.id);
        if (itemIndex > -1) {
            this.setActiveIndex(itemIndex);
        } else {
            this._selectedTab.next(item);
        }
    }

    goNext(): void {
        const current = this.getActiveIndex();
        this.setActiveIndex(current + 1);
    }

    goPrevious(): void {
        const current = this.getActiveIndex();
        this.setActiveIndex(current - 1);
    }

    goByTab(id: FeatureTab): void {
        const itemIndex = findIndex(this._tabs, (tab) => tab.id === id);
        if (itemIndex > -1) {
            this.setActiveIndex(itemIndex);
        }
    }

    async getTabs(event: EventModel): Promise<TabBarItem[]> {
        const { realCompetitionId, parentLeagueId, id } = event.league;
        const sportCompetitions = this.eventDetailsConfig.headerNavigationScoreboard[event.sport.id];
        let isScoreDefault = !!sportCompetitions;

        if (sportCompetitions?.length) {
            isScoreDefault = sportCompetitions.some((leagueId) => leagueId === realCompetitionId || leagueId === parentLeagueId || leagueId === id);
        }

        return await this.init(event, isScoreDefault);
    }

    private setMiddleColumnTab(): void {
        if (this.columnLayoutService.layout.isRightVisible) {
            this.setActiveIndex(0);
        }
    }

    private getActiveIndex(): number {
        return findIndex(this._tabs, (tab) => !!tab.active);
    }

    private setActiveIndex(index: number): void {
        if (index < 0) {
            index = this._tabs.length - 1;
        }
        if (index >= this._tabs.length) {
            index = 0;
        }

        this._tabs.forEach((tab, current) => (tab.active = current === index));
        this._tabs = this._tabs.slice(0);
        this._selectedTab.next(this._tabs[index]);
    }

    private getMediaSwitcherTabs(event: EventModel): string[] {
        const mediaSwitcherTabsBySport =
            this.eventDetailsConfig.mediaSwitcherTabs[event.sport.id] || this.eventDetailsConfig.mediaSwitcherTabs.anySport;

        return (
            (mediaSwitcherTabsBySport && mediaSwitcherTabsBySport[event.league.id]) ||
            mediaSwitcherTabsBySport?.anyCompetition ||
            this.mediaTabsFallback
        );
    }

    private getFeatureTabs(event: EventModel, isScoreDefault: boolean = false): TabBarItem[] {
        if (!event) {
            return [];
        }

        const tabs: TabBarItem[] = [];
        const activeTab = this.getActiveFeatureTab(event, isScoreDefault);
        const mediaSwitcherTabs = this.getMediaSwitcherTabs(event);

        const addTab = (id: number, icon: string, title: string): void => {
            tabs.push({ id, icon, active: id === activeTab, title: title || '' });
        };

        mediaSwitcherTabs.forEach((tab) => {
            switch (tab) {
                case MediaTab.Scoreboard:
                    addTab(FeatureTab.Scoreboard, 'sports-score', this.sitecoreEventDetails.eventDetails.Score);
                    break;
                case MediaTab.Video:
                    if (event.stream && event.stream.id) {
                        addTab(FeatureTab.Video, 'sports-video', this.sitecoreEventDetails.eventDetails.Video);
                    }
                    break;
                case MediaTab.Simulation:
                    if (
                        this.eventDetailsHelperService.hasAnimation(event) ||
                        this.eventDetailsHelperService.hasBetRadarVisualization(event) ||
                        this.eventDetailsHelperService.hasImgAnimationVisualization(event)
                    ) {
                        addTab(
                            FeatureTab.Simulation,
                            getAnimationIconForSport(event.sport.id, false),
                            this.sitecoreEventDetails.eventDetails.Animation,
                        );
                    }
                    if (event.live && event.runningBallDataId && event.participants?.length) {
                        addTab(FeatureTab.Simulation, 'sports-4-match', this.sitecoreEventDetails.eventDetails.Animation);
                    }
                    break;
                case MediaTab.Statistics:
                    if (this.eventDetailsHelperService.hasStats(event)) {
                        addTab(FeatureTab.Statistics, 'sports-stats', this.sitecoreEventDetails.eventDetails.Stats);
                    }
                    break;
            }
        });

        return tabs;
    }

    private getActiveFeatureTab(event: EventModel, isScoreDefault: boolean): FeatureTab {
        if (isScoreDefault) {
            return FeatureTab.Scoreboard;
        }
        if (
            this.eventDetailsHelperService.hasAnimation(event) ||
            this.eventDetailsHelperService.hasBetRadarVisualization(event) ||
            event.runningBallDataId ||
            this.eventDetailsHelperService.hasImgAnimationVisualization(event)
        ) {
            return FeatureTab.Simulation;
        }

        const urlTab = this.urlService.current().search.get('tab');

        for (const prop in FeatureUrl) {
            if (FeatureUrl[prop as keyof typeof FeatureUrl] === urlTab) {
                return FeatureTab[prop as keyof typeof FeatureUrl];
            }
        }

        return FeatureTab.Scoreboard;
    }
}
